import type { ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";

/**
 * The offer to reload, for whatever could not be drawn: a page
 * (`PageErrorBoundary`) and the whole app (`AppUnavailable`).
 *
 * One component, so the two cannot drift apart: the same mark, the same
 * hierarchy and the same button, and a reload that is only ever offered. It is
 * handed its words rather than reading them, because the app's fallback is
 * drawn above the language provider and the page's below it.
 *
 * It reads nothing else. No hook, no context and nothing from the server: this
 * is what stands when something could not be drawn, and it must not fail the
 * way that did.
 */

interface ReloadOfferProps {
    title: string;
    message: string;
    reloadLabel: string;
    /**
     * `page`: in the page's place, the shell still around it. `window`: the
     * whole window, with no shell left, so it brings its own background and
     * text colour, is the screen's one heading and is announced.
     */
    fill?: "page" | "window";
    /** Under the button: where to turn when the reload does not help. */
    note?: ReactNode;
}

export function ReloadOffer({ title, message, reloadLabel, fill = "page", note }: ReloadOfferProps) {
    const whole = fill === "window";
    const Heading = whole ? "h1" : "h2";

    return (
        <div
            role={whole ? "alert" : undefined}
            className={
                whole
                    ? "flex h-dvh items-center justify-center bg-background px-6 py-10 text-foreground"
                    : "flex min-h-[40vh] items-center justify-center px-6 py-10"
            }
        >
            <div className="flex max-w-md flex-col items-center gap-3 text-center">
                <AlertTriangle className="h-8 w-8 text-muted-foreground" aria-hidden />
                <Heading className="text-lg font-semibold">{title}</Heading>
                <p className="text-sm text-muted-foreground">{message}</p>
                <Button variant="outline" onClick={() => window.location.reload()}>
                    {reloadLabel}
                </Button>
                {note && <p className="text-xs text-muted-foreground">{note}</p>}
            </div>
        </div>
    );
}
